import _ from 'lodash'
import * as d3 from 'd3'
import d3Tip from 'd3-tip'
import classnames from 'classnames'

export default class ZeroBarChart {
  constructor (el, options) {
    this.el = el
    this._buildSvg()
    this._calcDimensions()
    this._buildChartContainer()
    this._setupResize()
    this._setupTips()
    let defaults = { duration: 750, verticalPaddingFactor: 0.1 }
    this.options = Object.assign({}, defaults, options || {})
  }

  update (data) {
    this.datapoints = _.cloneDeep(data).datapoints
    this.yFormat = data.yFormat
    this.min = data.min
    this.max = data.max
    this.styles = data.styles
    this.xAxisClass = data.xAxisClass
    this._draw()
  }

  get xDomain () {
    return this.datapoints.map(d => {
      return d.x
    })
  }

  _getPaddedYDomain () {
    let min = this.min
    let max = this.max

    // give the chart some breathing room, make it less dramatic
    min -= max * this.options.verticalPaddingFactor
    max += max * this.options.verticalPaddingFactor

    return [min, max]
  }

  _buildSvg () {
    this.svg = d3.select(this.el)
  }

  _buildChartContainer () {
    this.chart = this.svg
      .append('g')
      .attr('width', this.chartWidth)
      .attr('height', this.chartHeight)
      .attr('class', 'bar-chart__container zero-bar-chart__container')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`)
  }

  _draw () {
    if (!this.datapoints) {
      return
    }

    this._calcDimensions()
    this._calcScales()
    this._drawBars()
    this._drawXAxis()
    //this._drawYAxis()
  }

  _calcDimensions () {
    this.margin = { top: 5, right: 5, bottom: 20, left: 5 }
    this.outerWidth = this.el.getBoundingClientRect().width
    this.outerHeight = this.el.getBoundingClientRect().height
    this.chartWidth = this.outerWidth - this.margin.left - this.margin.right
    this.chartHeight = this.outerHeight - this.margin.top - this.margin.bottom
  }

  _calcScales () {
    this.scaleX = d3
      .scaleBand()
      .range([0, this.chartWidth])
      .domain(this.xDomain)
      .round(true)
      .padding(0.375)
    this.scaleY = d3
      .scaleLinear()
      .domain(this._getPaddedYDomain())
      .range([this.chartHeight, 0])
  }

  _drawBars () {
    this.bar = this.chart.selectAll('rect').data(this.datapoints)

    this.bar.exit().remove()

    const styles = this.styles
    const calcWidth = () => this.scaleX.bandwidth()
    const calcHeight = d =>
      Math.max(1, this.scaleY(0) - this.scaleY(Math.abs(d.y)))
    const calcX = d => this.scaleX(d.x)
    const calcY = d =>
      d.y > 0 ? this.scaleY(0) - calcHeight(d) : this.scaleY(0)
    const calcClass = d =>
      styles && d.cssSuffix
        ? classnames(d.cssSuffix.map(suffix => styles[`${suffix}-bar`]))
        : undefined

    this.bar
      .attr('class', calcClass)
      .transition()
      .duration(this.options.duration)
      .attr('x', calcX)
      .attr('y', calcY)
      .attr('width', calcWidth)
      .attr('height', calcHeight)

    this.bar
      .enter()
      .append('rect')
      .on('mouseover', this.tip.show)
      .on('mouseout', this.tip.hide)
      .attr('class', calcClass)
      .attr('x', calcX)
      .attr('y', calcY)
      .transition()
      .duration(this.options.duration)
      .attr('width', calcWidth)
      .attr('height', calcHeight)
  }

  _drawXAxis () {
    const xAxis = d3
      .axisBottom(
        this.scaleX
          .domain([1, this.datapoints.length])
          .range([0, this.chartWidth * 2 - 10])
      )
      .tickValues([1, this.datapoints.length])
      .tickFormat(d => `Year ${d}`)

    if (!this.xAxis) {
      this.xAxis = this.chart
        .append('g')
        .attr('class', `bar-chart__axis ${this.xAxisClass}`)
        .attr(
          'transform',
          `translate(${this.chartWidth / -2}, ${this.chartHeight})`
        )
        .call(xAxis)
    }

    this.xAxis.call(xAxis)
  }

  _drawYAxis () {
    const format = d3.format('.2s')
    const prefix = this.yFormat === 'money' ? '$' : ''

    const yAxis = d3
      .axisRight(this.scaleY)
      .tickSize(this.outerWidth)
      .tickArguments([3])
      .tickFormat(d => prefix + format(d))

    // Add to outer svg because it's outside the graph on the left
    // Prepend instead of append so that it's under the chart
    if (!this.yAxis) {
      this.yAxis = this.svg
        .insert('g', ':first-child')
        .attr('class', 'bar-chart__axis bar-chart__axis--y')
        .attr('transform', `translate(0, ${this.margin.top})`)
    }

    this.yAxis.call(yAxis)

    this.yAxis
      .selectAll('text')
      .attr('x', 0)
      .attr('dy', 4) // move text down half its height to vert align center
      .attr('transform', 'translate(0, 0)')
    this.yAxis
      .selectAll('line')
      .attr('transform', `translate(${this.margin.left}, 0)`)
  }

  _setupResize () {
    if (this._redrawChart) return

    const redraw = () => {
      this.chart.selectAll('*').remove()

      if (this.yAxis && this.yAxis.remove) {
        // Guard for early redraw lifecycle
        this.yAxis.remove()
      }
      if (this.xAxis && this.xAxis.remove) {
        // Guard for early redraw lifecycle
        this.xAxis.remove()
        this.xAxis = null
      }

      this._draw()
    }

    this._redrawChart = _.debounce(redraw, 250, { leading: false })

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this._redrawChart)
    }
  }

  _setupTips () {
    this.tip = d3Tip()
      .attr('class', 'chart-tip af hideMobile')
      .offset([-18, 0])
      .html(d => {
        return `${d.tooltipDescription}<em>${d.tooltipEmphasis}</em>`
      })

    this.chart.call(this.tip)
  }
}
